import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

// INTERFACES

// CONFIGS

// SERVICES
import {
  AbstractControl,
  Form,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';

import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { LocationApiService } from '../../../shared/services/api/location/location-api.service';
import { IBinLocation } from '../../interfaces';

@Component({
  selector: 'lum-location-warehouse-sku-transfer',
  templateUrl: './location-warehouse-sku-transfer.component.html',
  styleUrls: ['./location-warehouse-sku-transfer.component.scss'],
})
export class LocationWarehouseSkuTransferComponent implements OnInit, OnDestroy {
  locations :IBinLocation[] = [];
  loading :boolean = false;
  selectedSkuLocations$ :Observable<any>;
  selectedLocations :any[] = [];
  sku :any;
  submitLoading :boolean = false;
  form :UntypedFormGroup;

  private onDestroy$ = new Subject();

  constructor(
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private locationApiService :LocationApiService,
    private router :Router,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.selectedSkuLocations$ = this.locationApiService.selectedSkuLocations$;
    this.getSelectedSkuLocations().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onChangeEvent(keyword :any) :void {
    this.getLocations({ keyword });
  }

  onSelectEvent(selectedLocation :any) :void {
    this.form.patchValue({
      location: selectedLocation.id,
    });
  }

  onTransfer(values :any) :void {
    this.submitLoading = true;
    this.locationApiService
      .transferSkuLocation(values)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.submitLoading = false)),
      )
      .subscribe({
        next: (message) => {
          this.snackBar.open(
            message.success ||
              'SKU Stocks has been transferred to selected location.',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          this.router.navigate([
            '/warehouse-mobile-app/location-selection/',
            this.sku.id,
          ]);
        },
        error: (err) => {
          this.snackBar.open(
            err.error || 'Unable to transferred to selected location.',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private getSelectedSkuLocations() :Observable<any> {
    return this.selectedSkuLocations$.pipe(
      takeUntil(this.onDestroy$),
      map((data) => {
        const { sku, selectedWarehouses } = data;
        this.selectedLocations = selectedWarehouses;
        this.sku = sku;
        this.initForm();

        return selectedWarehouses;
      }),
    );
  }

  private initForm() :void {
    this.form = this.fb.group({
      location: ['', Validators.required],
      sourceLocations: this.selectedLocations.length
        ? this.fb.array(
            this.selectedLocations.map((selectedLocation :any) => {
              const {
                id,
                pivotId,
                warehouseId,
                warehouseName,
                locationId,
                location,
                lotNumber,
                productionDate,
                quantity,
              } = selectedLocation;
              const defaultQty = quantity < 0 ? 0 : quantity;

              return this.fb.group({
                id: [id],
                warehouseId: [warehouseId],
                warehouseName: [warehouseName],
                locationId: [locationId],
                rfqStockLocationId: [pivotId],
                location: [location],
                lotNumber: [lotNumber],
                productionDate: [productionDate],
                onhandQty: [quantity || 0],
                quantity: [
                  defaultQty || 0,
                  [Validators.required, this.quantityValidator(quantity || 0)],
                ],
              });
            }),
          )
        : this.fb.array([]),
    });
  }

  private getLocations(params :{ keyword :string }) :void {
    this.loading = true;
    this.locationApiService
      .getAllV2({
        keyword: params.keyword,
        limit: '10',
        relations: 'warehouseGroups',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((locations) => {
        if (!locations.length) {
          this.snackBar.open('No location found', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
        this.locations = locations.map((location :IBinLocation) => ({
          ...location,
          locationDisplay: `${location.location_name} (${location.parent?.location_name})`,
        }));
        this.loading = false;
        this.cd.detectChanges();
      });
  }

  private quantityValidator(onhandQty :number) :ValidatorFn {
    return (control :AbstractControl) :{ [key :string] :any } | null => {
      // If onhandQty is negative, consider the value as valid
      if (onhandQty < 0) {
        return null;
      }

      const valid = control.value >= 0 && control.value <= onhandQty;

      return valid ? null : { invalidQuantity: { value: control.value } };
    };
  }

  get formValues() :any {
    return this.form.value;
  }

  get warehousesFormArray() :UntypedFormArray {
    return this.form.get('sourceLocations') as UntypedFormArray;
  }

  get locationFormControlValue() :any {
    return this.form.get('location')?.value;
  }
}
